import { useId } from "react";

import { Button } from "./button";
import "./error-fallback.css";

export type ErrorFallbackVariant = "page" | "section" | "inline";

export type ErrorFallbackAction = {
  label: string;
  onClick: () => void;
};

export interface ErrorFallbackProps {
  title: string;
  message: string;
  variant?: ErrorFallbackVariant;
  primaryAction?: ErrorFallbackAction;
  secondaryAction?: ErrorFallbackAction;
}

/**
 * Fixed-copy recovery UI for an unexpected React failure. The thrown value is deliberately not a
 * prop: technical details belong in RUM, never in a user-facing surface where they could leak
 * project or identity data. The urgent text is the alert; interactive actions are siblings because
 * `role="alert"` is intended for text, not controls.
 */
export function ErrorFallback({
  title,
  message,
  variant = "section",
  primaryAction,
  secondaryAction,
}: ErrorFallbackProps) {
  const titleId = useId();

  return (
    <section
      className={`rc-error-fallback rc-error-fallback--${variant}`}
      aria-labelledby={titleId}
    >
      <div className="rc-error-fallback__panel">
        <div className="rc-error-fallback__alert" role="alert">
          <svg className="rc-error-fallback__icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3.25 21 19H3L12 3.25Z" />
            <path d="M12 8.25v5.5M12 17.1v.15" />
          </svg>
          <div>
            {variant === "page" ? (
              <h1 id={titleId} className="rc-error-fallback__title">
                {title}
              </h1>
            ) : (
              <h2 id={titleId} className="rc-error-fallback__title">
                {title}
              </h2>
            )}
            <p className="rc-error-fallback__message">{message}</p>
          </div>
        </div>

        {primaryAction || secondaryAction ? (
          <div className="rc-error-fallback__actions">
            {primaryAction ? (
              <Button
                className="rc-error-fallback__action"
                size="lg"
                variant="solid"
                tone="accent"
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </Button>
            ) : null}
            {secondaryAction ? (
              <Button
                className="rc-error-fallback__action"
                size="lg"
                variant="outline"
                tone="neutral"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
